import { useContext, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Api } from '@services';
import '../../dist/azure-maps-leaflet';
import '../../dist/Leaflet.ImageOverlay.Rotated.js';
import 'leaflet/dist/leaflet.css';
import { ThemeContext } from '../../context/ThemeContext';
import { Loading } from '../../components/utils/';
import { getMapIcon } from '@screens/Maps/Map/MapObject/MapComponents/MapMiscComponents/MapIcons';

interface AssetDetailMapProps {
  lastLocation: any;
  asset: any;
}

// Helper to determine Azure Maps domain directly from host context
const getAzureMapsDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isGov =
    hostname.endsWith('.gov') || hostname.includes('azure-gov') || hostname.includes('usgov');
  return isGov ? 'atlas.azure.us' : 'atlas.microsoft.com';
};

export const AssetMap = ({ lastLocation, asset }: AssetDetailMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const [mapGeoJSON, setMapGeoJSON] = useState<any>(null);
  const [mapKey, setMapKey] = useState<string | null>(null);
  const [mapImgUrl, setMapImgUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { theme } = useContext(ThemeContext);

  const getColor = (status: any) => {
    const statusKey = Number(status);
    switch (statusKey) {
      case 1:
        return '--c-primary';
      case 2:
        return '--warning';
      case 3:
        return '--secondary';
      case 0:
      default:
        return '--danger';
    }
  };

  // 1. Fetch Azure Maps Key
  useEffect(() => {
    Api.get(`/me/map-server-key`)
      .then((resp) => {
        setMapKey(resp.data as string);
      })
      .catch((err) => {
        console.error('Failed to fetch map key:', err);
      });
  }, []);

  // 2. Fetch GeoJSON and Image Blob
  useEffect(() => {
    let blobUrl = '';
    const getOverlayData = async () => {
      if (!lastLocation?.mapId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const { data } = await Api.get(`v2/maps/${lastLocation.mapId}`);
        setMapGeoJSON(data);

        const response = await Api.get(`v2/maps/${lastLocation.mapId}/image`, {
          responseType: 'blob',
        });

        blobUrl = URL.createObjectURL(response.data as Blob);
        setMapImgUrl(blobUrl);
      } catch (error) {
        console.error('Failed to fetch map overlay:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getOverlayData();

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [lastLocation?.mapId]);

  // 3. Dynamic Tile Updates on Theme Toggle (Without destroying map)
  useEffect(() => {
    if (!tileLayerRef.current) return;

    const tilesetId = theme === 'dark' ? 'microsoft.base.darkgrey' : 'microsoft.base.road';

    if (typeof (tileLayerRef.current as any).setOptions === 'function') {
      (tileLayerRef.current as any).setOptions({ tilesetId });
    } else if (typeof tileLayerRef.current.setUrl === 'function') {
      const azMapsDomain = getAzureMapsDomain();
      const newUrl = `https://${azMapsDomain}/map/tile?subscription-key=${mapKey}&api-version=2.0&tilesetId=${tilesetId}&zoom={z}&x={x}&y={y}&tileSize=256&language=en-US&view=Auto`;
      tileLayerRef.current.setUrl(newUrl);
    }
  }, [theme, mapKey]);

  // 4. Map Initialization & Base Tile Setup
  useEffect(() => {
    if (!mapContainerRef.current || !lastLocation || !mapGeoJSON || isLoading) return;

    const isStatic = mapGeoJSON.features?.[0]?.properties?.MapType === 'Static';
    const point = lastLocation?.geoCoordinates?.coordinates;

    const lng = point?.x;
    const lat = point?.y;

    if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) return;

    const markerPos: [number, number] = [lat, lng];

    const map = L.map(mapContainerRef.current, {
      attributionControl: false,
      maxZoom: 25,
    }).setView(markerPos, isStatic ? 18 : 19);

    mapRef.current = map;

    // Attach Azure Tile Layer
    if (!isStatic && mapKey) {
      const azMapsDomain = getAzureMapsDomain();
      const tilesetId = theme === 'dark' ? 'microsoft.base.darkgrey' : 'microsoft.base.road';

      const layer = (L.tileLayer as any)
        .azureMaps({
          authOptions: { authType: 'subscriptionKey', subscriptionKey: mapKey, azMapsDomain },
          tilesetId,
          maxNativeZoom: 18,
          maxZoom: 25,
          crossOrigin: 'anonymous',
        })
        .addTo(map);

      tileLayerRef.current = layer;
    }

    // Determine Icon based on status
    const assetIcon = getMapIcon('asset', getColor(asset?.status), false, 45);
    L.marker(markerPos, { icon: assetIcon }).addTo(map);

    // Refresh map size for React
    const timeoutId = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        tileLayerRef.current = null;
      }
    };
  }, [lastLocation, mapKey, mapGeoJSON, isLoading]);

  // 5. Separate Effect for Rotated Overlay
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapImgUrl || !mapGeoJSON) return;

    const poly = mapGeoJSON.features?.[0]?.geometry?.coordinates?.[0];
    const isStatic = mapGeoJSON.features?.[0]?.properties?.MapType === 'Static';

    if (poly && poly.length >= 4) {
      try {
        const topLeft = L.latLng(poly[3][1], poly[3][0]);
        const topRight = L.latLng(poly[2][1], poly[2][0]);
        const bottomLeft = L.latLng(poly[0][1], poly[0][0]);

        // @ts-ignore
        const overlay = L.imageOverlay.rotated(mapImgUrl, topLeft, topRight, bottomLeft, {
          opacity: 1,
          interactive: false,
        });

        overlay.addTo(map);

        if (isStatic) {
          map.fitBounds(L.latLngBounds([topLeft, topRight, bottomLeft]));
        }

        return () => {
          if (map.hasLayer(overlay)) {
            map.removeLayer(overlay);
          }
        };
      } catch (e) {
        console.error('Error rendering rotated overlay:', e);
      }
    }
  }, [mapImgUrl, mapGeoJSON]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height: '100%',
        background: theme === 'dark' ? '#111' : '#ddd',
        borderRadius: '4px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.1)',
          }}
        >
          <Loading />
        </div>
      )}
    </div>
  );
};
